import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import axios from "axios";

const Payments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonUsers, setLessonUsers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterBy, setFilterBy] = useState('payment');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/history/layout`,
        { headers: { "Access-Token": process.env.REACT_APP_API_TOKEN } }
      );
      setLessons(response.data.payments);
    } catch (error) {
      console.error("Failed to fetch lessons:", error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas ładowania danych o płatnościach',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open dialog for selected lesson
  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
    setLessonUsers({
      payed: lesson.payed,
      not_payed: lesson.not_payed,
      attended: lesson.attended,
      not_attended: lesson.not_attended,
    });
    setDialogOpen(true);
  };

  const handleFilterChange = (_, newFilter) => {
    if (newFilter !== null) {
      setFilterBy(newFilter);
    }
  };

  const updateUserStatus = async (userId, lessonId, statusType, newValue) => {
    console.log('=== updateUserStatus CALLED ===');
    console.log('Parameters:', { userId, lessonId, statusType, newValue });
    
    try {
      const endpoint = statusType === 'payment' 
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${lessonId}/users/${userId}/payment`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${lessonId}/users/${userId}/attendance`;
      
      const payload = statusType === 'payment' 
        ? { payed: newValue }
        : { attended: newValue };

      console.log('Endpoint:', endpoint);
      console.log('Payload:', payload);
      console.log('Headers:', { "Access-Token": process.env.REACT_APP_API_TOKEN });

      const patchResponse = await axios.patch(endpoint, payload, {
        headers: { "Access-Token": process.env.REACT_APP_API_TOKEN }
      });
      
      console.log('Backend response:', patchResponse);

      // Refresh lesson data to get updated categorization
      const refreshResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/history/layout`,
        { headers: { "Access-Token": process.env.REACT_APP_API_TOKEN } }
      );
      
      console.log('Refresh response:', refreshResponse);
      
      const updatedLesson = refreshResponse.data.payments.find(l => l.lesson_id === selectedLesson.lesson_id);
      if (updatedLesson) {
        setLessonUsers({
          payed: updatedLesson.payed,
          not_payed: updatedLesson.not_payed,
          attended: updatedLesson.attended,
          not_attended: updatedLesson.not_attended,
        });
      }

      setSnackbar({
        open: true,
        message: `Status użytkownika został zaktualizowany`,
        severity: 'success'
      });

    } catch (error) {
      console.error('Failed to update user status:', error);
      setSnackbar({
        open: true,
        message: `Błąd podczas aktualizacji statusu`,
        severity: 'error'
      });
    }
  };

  const getUserCurrentStatus = (user, statusType) => {
    if (statusType === 'payment') {
      // Check if user is in payed or not_payed list
      const isPayed = lessonUsers.payed.some(u => u.id === user.id);
      return isPayed ? 'paid' : 'not_paid';
    } else {
      // Check if user is in attended or not_attended list
      const isAttended = lessonUsers.attended.some(u => u.id === user.id);
      const isNotAttended = lessonUsers.not_attended.some(u => u.id === user.id);
      if (isAttended) return 'attended';
      if (isNotAttended) return 'not_attended';
      return 'unknown';
    }
  };

  const getAllUsers = () => {
    if (!lessonUsers) return [];
    
    // Combine all users and remove duplicates
    const allUsers = [
      ...lessonUsers.payed,
      ...lessonUsers.not_payed,
      ...lessonUsers.attended,
      ...lessonUsers.not_attended
    ];
    
    // Remove duplicates based on user ID
    const uniqueUsers = allUsers.filter((user, index, self) => 
      index === self.findIndex(u => u.id === user.id)
    );
    
    return uniqueUsers;
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLesson(null);
    setLessonUsers(null);
  };

  const formatLessonDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalUsers = (lesson) => {
    const paidCount = lesson.payed?.length || 0;
    const unpaidCount = lesson.not_payed?.length || 0;
    return paidCount + unpaidCount;
  };

  const getPaidCount = (lesson) => {
    return lesson.payed?.length || 0;
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Zarządzanie Płatnościami
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Przeglądaj i zarządzaj płatnościami za zajęcia oraz obecnością uczestników
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Box textAlign="center">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">
              Ładowanie danych o płatnościach...
            </Typography>
          </Box>
        </Box>
      ) : lessons.length > 0 ? (
        <Box sx={{ 
          display: 'grid', 
          gap: 2, 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(auto-fit, minmax(300px, 1fr))',
            md: 'repeat(auto-fill, minmax(350px, 1fr))'
          }
        }}>
          {lessons.map((lesson) => {
            const totalUsers = getTotalUsers(lesson);
            const paidUsers = getPaidCount(lesson);
            const paymentPercentage = totalUsers > 0 ? (paidUsers / totalUsers) * 100 : 0;

            return (
              <Card 
                key={lesson.lesson_id}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  }
                }}
                onClick={() => handleLessonClick(lesson)}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PaymentIcon sx={{ mr: 1, color: '#667eea', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                    <Typography 
                      variant="h6" 
                      component="div"
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      {lesson.lesson_name}
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {formatLessonDate(lesson.lesson_start)}
                  </Typography>

                  <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                    <Chip
                      size="small"
                      icon={<PeopleIcon sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }} />}
                      label={`${totalUsers} uczestników`}
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                    />
                    <Chip
                      size="small"
                      icon={<CheckCircleIcon sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }} />}
                      label={`${paidUsers} zapłaciło`}
                      color={paymentPercentage === 100 ? 'success' : paymentPercentage > 50 ? 'warning' : 'error'}
                      variant="filled"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                    />
                  </Box>

                  <Box mt={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Płatności
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {Math.round(paymentPercentage)}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: { xs: 6, sm: 8 },
                        backgroundColor: '#e0e0e0',
                        borderRadius: 4,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${paymentPercentage}%`,
                          height: '100%',
                          backgroundColor: paymentPercentage === 100 ? '#4caf50' : paymentPercentage > 50 ? '#ff9800' : '#f44336',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PaymentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Brak danych o płatnościach
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nie znaleziono żadnych zajęć z danymi o płatnościach
          </Typography>
        </Paper>
      )}

      {/* Dialog for users in a selected lesson */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon color="primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {selectedLesson?.lesson_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {selectedLesson?.lesson_start && formatLessonDate(selectedLesson.lesson_start)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Filter Toggle */}
          <Box sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Wybierz widok:
            </Typography>
            <ToggleButtonGroup
              value={filterBy}
              exclusive
              onChange={handleFilterChange}
              aria-label="filter by"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            >
              <ToggleButton value="payment" aria-label="payment filter">
                <PaymentIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                Płatności
              </ToggleButton>
              <ToggleButton value="attendance" aria-label="attendance filter">
                <PeopleIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                Obecność
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Użytkownik
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Status i akcje
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lessonUsers && getAllUsers().map((user) => {
                  const currentStatus = getUserCurrentStatus(user, filterBy);
                  
                  return (
                    <TableRow 
                      key={`user-${user.id}`}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        }
                      }}
                    >
                      <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {user.name}
                        </Typography>
                      </TableCell>
                      
                      {/* Combined Status and Action Column */}
                      <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: { xs: 1, sm: 2 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' }
                        }}>
                          {/* Current Status Chip */}
                          {filterBy === 'payment' && (
                            <Chip
                              label={currentStatus === 'paid' ? 'Zapłacił' : 'Nie zapłacił'}
                              color={currentStatus === 'paid' ? 'success' : 'error'}
                              variant="filled"
                              size="small"
                              sx={{ fontSize: { xs: '0.65rem', sm: '0.8125rem' } }}
                            />
                          )}
                          {filterBy === 'attendance' && (
                            <Chip
                              label={
                                currentStatus === 'attended' ? 'Obecny' : 
                                currentStatus === 'not_attended' ? 'Nieobecny' : 
                                'Brak danych'
                              }
                              color={
                                currentStatus === 'attended' ? 'success' : 
                                currentStatus === 'not_attended' ? 'error' : 
                                'default'
                              }
                              variant="filled"
                              size="small"
                              sx={{ fontSize: { xs: '0.65rem', sm: '0.8125rem' } }}
                            />
                          )}
                          
                          {/* Action Chip - Single Toggle Button */}
                          {filterBy === 'payment' && (
                            user.pass_id ? (
                              // User has a pass - show info chip, not clickable
                              <Chip
                                label={isMobile ? "Karnet" : "Zapisano karnetem (nie można zmienić)"}
                                color="info"
                                variant="filled"
                                size="small"
                                sx={{
                                  cursor: 'default',
                                  fontSize: { xs: '0.65rem', sm: '0.8125rem' }
                                }}
                              />
                            ) : (
                              // User doesn't have a pass - show clickable toggle
                              <Chip
                                label={isMobile 
                                  ? (currentStatus === 'paid' ? 'Nie zapłacone' : 'Zapłacone')
                                  : (currentStatus === 'paid' ? 'Oznacz jako nie zapłacone' : 'Oznacz jako zapłacone')
                                }
                                color={currentStatus === 'paid' ? 'error' : 'success'}
                                variant="outlined"
                                size="small"
                                clickable
                                onClick={() => {
                                  const newStatus = currentStatus !== 'paid';
                                  console.log('Payment chip clicked, new status:', newStatus);
                                  updateUserStatus(user.id, selectedLesson.lesson_id, 'payment', newStatus);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  fontSize: { xs: '0.65rem', sm: '0.8125rem' },
                                  '&:hover': {
                                    backgroundColor: currentStatus === 'paid' ? 'error.light' : 'success.light',
                                    color: 'white'
                                  }
                                }}
                              />
                            )
                          )}
                          {filterBy === 'attendance' && (
                            <Chip
                              label={isMobile
                                ? (currentStatus === 'attended' ? 'Nieobecny' :
                                   currentStatus === 'not_attended' ? 'Obecny' :
                                   'Obecny')
                                : (currentStatus === 'attended' ? 'Oznacz jako nieobecny' :
                                   currentStatus === 'not_attended' ? 'Oznacz jako obecny' :
                                   'Oznacz jako obecny')
                              }
                              color={
                                currentStatus === 'attended' ? 'error' :
                                currentStatus === 'not_attended' ? 'success' :
                                'success'
                              }
                              variant="outlined"
                              size="small"
                              clickable
                              onClick={() => {
                                const newStatus = currentStatus !== 'attended';
                                console.log('Attendance chip clicked, new status:', newStatus);
                                updateUserStatus(user.id, selectedLesson.lesson_id, 'attendance', newStatus);
                              }}
                              sx={{
                                cursor: 'pointer',
                                fontSize: { xs: '0.65rem', sm: '0.8125rem' },
                                '&:hover': {
                                  backgroundColor: currentStatus === 'attended' ? 'error.light' : 'success.light',
                                  color: 'white'
                                }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleCloseDialog} color="error" variant={isMobile ? "contained" : "text"}>
            Zamknij
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payments;
